import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Camera, Paperclip, Send, Trash2, X } from 'lucide-react';
import DynamicsIcon from '../common/DynamicsIcon';
import backendApi from '../../services/backendApi';
import { readTicketFile, selectTicketFiles } from '../../utils/ticketAttachments';

const PROBLEM_PROMPT = 'Décrivez votre problème avec vos mots : ce que vous essayez de faire, où cela se passe et ce qui se produit. Vous pouvez raconter toute la situation dès maintenant.';

export default function SupportTriageModal({ projectId, anchor, onClose, onComplete, onSearchComplete }) {
  const [phase, setPhase] = useState('choice');
  const [requestType, setRequestType] = useState('');
  const [answers, setAnswers] = useState({ problem: '', reproduction: '', actual: '' });
  const [messages, setMessages] = useState([{ role: 'bot', text: 'Bonjour, je suis Dynamix. De quoi avez-vous besoin ?' }]);
  const [clarificationCount, setClarificationCount] = useState(0);
  const [clarificationField, setClarificationField] = useState('clarification');
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [screenshot, setScreenshot] = useState(null);
  const [result, setResult] = useState(null);
  const [createdTicket, setCreatedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [readingFiles, setReadingFiles] = useState(false);
  const [error, setError] = useState('');
  const chatBodyRef = useRef(null);
  const busy = loading || readingFiles;

  useEffect(() => {
    if (chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [messages, phase, error]);

  useEffect(() => {
    if (screenshot) setMessages(previous => previous.some(message => message.role === 'screenshot')
      ? previous : [...previous, { role: 'screenshot', image: screenshot }]);
  }, [screenshot]);

  const addFiles = async event => {
    const files = Array.from(event.target.files || []);
    const selection = selectTicketFiles(files, attachments);
    event.target.value = '';
    setError(selection.error);
    if (!selection.accepted.length) return;
    setReadingFiles(true);
    try {
      const added = await Promise.all(selection.accepted.map(readTicketFile));
      setAttachments(previous => [...previous, ...added]);
    } catch { setError('Impossible de lire une pièce jointe.'); }
    finally { setReadingFiles(false); }
  };

  const saveScreenshot = async file => {
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('La capture doit être une image PNG, JPEG ou WebP.'); return;
    }
    if (file.size > 10 * 1024 * 1024) { setError(`${file.name} dépasse 10 Mo.`); return; }
    setReadingFiles(true);
    try {
      const value = await readTicketFile(file);
      const selection = selectTicketFiles([file], attachments.filter(item => item !== screenshot && item.name !== value.name));
      if (selection.error) { setError(selection.error); return; }
      const nextAttachments = [...attachments.filter(item => item !== screenshot && item.name !== value.name), value];
      setScreenshot(value);
      setAttachments(nextAttachments);
      setMessages(previous => [...previous.filter(message => message.role !== 'screenshot'), { role: 'screenshot', image: value }]);
      setError('');
      if (phase === 'errorMessage') {
        const nextAnswers = { ...answers, actual: '', problem: answers.reproduction };
        setDraft('');
        await prepareTicket(nextAnswers, clarificationCount, value, nextAttachments, true);
      } else if (phase === 'clarification') {
        const nextAnswers = { ...answers };
        if (clarificationField === 'actual') nextAnswers.actual = '';
        else nextAnswers.clarification = [answers.clarification, 'Capture d’écran jointe'].filter(Boolean).join('\n');
        setDraft('');
        setClarificationCount(2);
        await prepareTicket(nextAnswers, 2, value, nextAttachments, true);
      }
    } catch { setError('Impossible de lire cette capture.'); }
    finally { setReadingFiles(false); }
  };

  const selectScreenshot = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) await saveScreenshot(file);
  };

  const pasteScreenshot = event => {
    if (busy || ['choice', 'searching', 'success'].includes(phase)) return;
    const image = Array.from(event.clipboardData?.items || []).find(item => item.kind === 'file' && item.type.startsWith('image/'))?.getAsFile();
    if (!image) return;
    event.preventDefault();
    const extension = image.type === 'image/jpeg' ? 'jpg' : image.type === 'image/webp' ? 'webp' : 'png';
    saveScreenshot(new File([image], `capture-collee-${Date.now()}.${extension}`, { type: image.type }));
  };

  const captureBrowserTab = async () => {
    if (!navigator.mediaDevices?.getDisplayMedia || typeof window.CaptureController !== 'function' || !window.CaptureController.prototype?.setFocusBehavior) {
      setError('Ce navigateur ne peut pas garder le chat au premier plan pendant la capture. Collez ou joignez une capture à la place.');
      return;
    }
    let stream;
    setError('');
    try {
      const controller = new window.CaptureController();
      controller.setFocusBehavior('no-focus-change');
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { displaySurface: 'browser' }, audio: false,
        preferCurrentTab: false, selfBrowserSurface: 'exclude', surfaceSwitching: 'exclude', monitorTypeSurfaces: 'exclude',
        controller });
      const surface = stream.getVideoTracks()[0]?.getSettings?.().displaySurface;
      if (surface && surface !== 'browser') {
        setError('Choisissez un onglet de votre navigateur dans la fenêtre de partage, puis réessayez.');
        return;
      }
      const video = document.createElement('video');
      const videoReady = new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
      });
      video.srcObject = stream;
      await videoReady;
      await video.play();
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('Capture vide');
      await saveScreenshot(new File([blob], `capture-onglet-${Date.now()}.png`, { type: 'image/png' }));
    } catch (captureError) {
      if (captureError?.name !== 'NotAllowedError') setError('Impossible de capturer cet onglet. Vous pouvez coller ou joindre une capture.');
    } finally {
      stream?.getTracks().forEach(track => track.stop());
    }
  };

  const createTicket = async (qualification, ticketAttachments = attachments) => {
    setLoading(true);
    setError('');
    try {
      const data = await backendApi.finalizeSupportTriage({
        qualificationToken: qualification.qualificationToken, outcome: 'escalated', confirmed: true, attachments: ticketAttachments,
      });
      setCreatedTicket(data.ticket || { id: data.ticketId });
      setPhase('success');
      try { await onComplete?.(data.ticket); }
      catch (refreshError) { console.error('Le ticket est créé, mais la liste ne s’est pas actualisée :', refreshError); }
    } catch (requestError) {
      setError(requestError.message);
      setPhase('createError');
    } finally { setLoading(false); }
  };

  const prepareTicket = async (nextAnswers, nextClarificationCount = clarificationCount, currentScreenshot = screenshot, currentAttachments = attachments, fromScreenshot = false) => {
    if (busy && !fromScreenshot) return;
    setAnswers(nextAnswers);
    setPhase('searching');
    setLoading(true);
    setError('');
    try {
      const qualification = await backendApi.triageSupport({
        project_id: Number(projectId), requestType, answers: nextAnswers,
        message: nextAnswers.problem,
        chatMode: true, skipSimilarity: true, clarificationCount: nextClarificationCount,
        ...(currentScreenshot ? { screenshot: currentScreenshot } : {}),
      });
      if (qualification.status === 'collecting') {
        setClarificationField(qualification.clarificationTarget || qualification.missingFields?.[0] || 'clarification');
        setMessages(previous => [...previous, { role: 'bot', text: qualification.nextQuestion || 'Pouvez-vous préciser votre demande ?' }]);
        setPhase('clarification');
        return;
      }
      setResult(qualification);
      setMessages(previous => [...previous, { role: 'bot', text: 'Merci, j’ai les informations nécessaires. Je crée votre ticket.' }]);
      await createTicket(qualification, currentAttachments);
    } catch (requestError) {
      setError(requestError.message);
      setPhase(nextClarificationCount > 0 ? 'clarification' : requestType === 'incident' ? 'errorMessage' : 'request');
    } finally { setLoading(false); }
  };

  const chooseType = type => {
    setRequestType(type);
    setAnswers({ problem: '', reproduction: '', actual: '' });
    setDraft('');
    setError('');
    setClarificationCount(0);
    setMessages([{ role: 'bot', text: 'Bonjour, je suis Dynamix. De quoi avez-vous besoin ?' },
      { role: 'user', text: type === 'incident' ? 'Signaler un problème' : type === 'search' ? 'Chercher un ticket' : 'Faire une demande' },
      { role: 'bot', text: type === 'incident' ? PROBLEM_PROMPT : type === 'search' ? 'Quel est le sujet du ticket recherché ? Décrivez-le avec vos mots.' : 'Quelle est votre question ou votre demande ?' },
      ...(screenshot ? [{ role: 'screenshot', image: screenshot }] : [])]);
    setPhase(type === 'incident' ? 'occurrence' : type === 'search' ? 'ticketSearch' : 'request');
  };

  const submitTicketSearch = async event => {
    event.preventDefault();
    const query = draft.trim();
    if (!query || busy) return;
    setMessages(previous => [...previous,{role:'user',text:query}]);
    setLoading(true);
    setPhase('searchingTickets');
    setError('');
    try {
      const result = await backendApi.searchTicketsWithAI(Number(projectId),query);
      onSearchComplete?.(query,result.results || []);
      onClose();
    } catch (searchError) {
      setError(searchError.message);
      setPhase('ticketSearch');
    } finally { setLoading(false); }
  };

  const submitOccurrence = event => {
    event.preventDefault();
    if (!draft.trim()) return;
    setAnswers(previous => ({ ...previous, reproduction: draft.trim() }));
    setMessages(previous => [...previous, { role: 'user', text: draft.trim() }, { role: 'bot', text: 'Quel message d’erreur voyez-vous ? Vous pouvez le recopier, joindre une capture d’écran ou un fichier qui montre le problème.' }]);
    setDraft('');
    setPhase('errorMessage');
  };

  const submitErrorMessage = event => {
    event.preventDefault();
    const actual = draft.trim();
    if (!actual && !attachments.length) return;
    const nextAnswers = { ...answers, actual: actual || (screenshot ? 'Voir la capture d’écran jointe' : 'Voir les pièces jointes'),
      problem: actual ? `${answers.reproduction} — ${actual}` : answers.reproduction };
    if (actual) setMessages(previous => [...previous, { role: 'user', text: actual }]);
    setDraft('');
    const nextCount = !actual && attachments.length ? 2 : clarificationCount;
    setClarificationCount(nextCount);
    prepareTicket(nextAnswers, nextCount);
  };

  const submitRequest = event => {
    event.preventDefault();
    if (!draft.trim()) return;
    const request = draft.trim();
    setMessages(previous => [...previous, { role: 'user', text: request }]);
    setDraft('');
    prepareTicket({ ...answers, problem: request });
  };

  const submitClarification = event => {
    event.preventDefault();
    const response = draft.trim();
    if (!response && !attachments.length) return;
    const value = response || (screenshot ? 'Capture jointe' : 'Pièces jointes');
    const nextAnswers = { ...answers };
    if (clarificationField === 'reproduction' && response) nextAnswers.reproduction = [answers.reproduction, response].filter(Boolean).join('\n');
    else if (clarificationField === 'actual') nextAnswers.actual = response || [answers.actual, 'Voir les pièces jointes'].filter(Boolean).join('\n');
    else nextAnswers.clarification = [answers.clarification, value].filter(Boolean).join('\n');
    setMessages(previous => [...previous, { role: 'user', text: value }]);
    setDraft('');
    const nextCount = attachments.length && !response ? 2 : clarificationCount + 1;
    setClarificationCount(nextCount);
    prepareTicket(nextAnswers, nextCount);
  };

  const skipClarification = () => {
    setMessages(previous => [...previous, { role: 'user', text: 'Je n’ai pas cette information pour le moment.' }]);
    setDraft('');
    setClarificationCount(2);
    prepareTicket({ ...answers, clarification: [answers.clarification, 'Information non disponible pour le moment.'].filter(Boolean).join('\n') }, 2);
  };

  const showScreenshotActions = !screenshot && (phase === 'errorMessage' || phase === 'clarification');

  return <div className="assistance-chat-overlay" role="dialog" aria-modal="true" aria-label="Assistance Dynamix" onPaste={pasteScreenshot}>
    <div className="assistance-chat-panel dashboard-modal__surface flex w-full flex-col bg-white shadow-2xl" style={anchor ? { '--assistance-anchor-top': `${anchor.top}px`, '--assistance-anchor-right': `${anchor.right}px` } : undefined}>
      <button type="button" className="assistance-chat-close" onClick={onClose} disabled={busy} aria-label="Fermer"><X /></button>
      <div ref={chatBodyRef} className="flex-1 space-y-5 overflow-y-auto p-6" aria-live="polite">
        {error && <div role="alert" className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"><AlertCircle size={18} />{error}</div>}
        {messages.map((message, index) => message.role === 'bot' ? <BotBubble key={index}>{message.text}</BotBubble> : message.role === 'screenshot'
          ? <ScreenshotBubble key={index} image={message.image} disabled={busy || phase === 'success'} onRemove={() => { setAttachments(previous => previous.filter(item => item !== message.image)); setScreenshot(null); setMessages(previous => previous.filter(item => item !== message)); }} />
          : <UserBubble key={index}>{message.text}</UserBubble>)}
        {attachments.filter(file => file !== screenshot).map((file, index) => <div className="assistance-attached-file" key={`${file.name}-${index}`}><Paperclip size={15} /><span>{file.name}</span><button type="button" disabled={busy} aria-label={`Supprimer ${file.name}`} onClick={() => setAttachments(current => current.filter(item => item !== file))}><Trash2 size={16} /></button></div>)}
        {phase === 'choice' && <div className="flex flex-wrap gap-3 pl-11"><button type="button" className="assistance-primary" onClick={() => chooseType('incident')}>Signaler un problème</button><button type="button" className="assistance-primary" onClick={() => chooseType('request')}>Faire une demande</button><button type="button" className="assistance-primary" onClick={() => chooseType('search')}>Chercher un ticket</button></div>}
        {phase === 'occurrence' && <AnswerForm label="Description du problème" placeholder="Ex. : Dans Ventes, je valide une commande et rien ne se passe. J’ai réessayé plusieurs fois…" value={draft} onChange={setDraft} onSubmit={submitOccurrence} disabled={busy} />}
        {phase === 'errorMessage' && <><AnswerForm label="Message d’erreur" placeholder="Ex. : Accès refusé lors de la validation…" value={draft} onChange={setDraft} onSubmit={submitErrorMessage} disabled={busy} allowEmpty={attachments.length > 0} submitLabel={!draft.trim() && attachments.length ? 'Continuer avec les pièces jointes' : 'Continuer'} options={showScreenshotActions && <CaptureOptions addFiles={addFiles} selectScreenshot={selectScreenshot} captureBrowserTab={captureBrowserTab} disabled={busy} />} /><div className="pl-11"><button type="button" className="text-sm font-semibold text-[#6B4C9A] underline" onClick={() => setDraft('Aucun message d’erreur')}>Aucun message d’erreur ne s’affiche</button></div></>}
        {phase === 'request' && <AnswerForm label="Votre question ou demande" placeholder="Ex. : Je souhaite accéder au rapport des ventes…" value={draft} onChange={setDraft} onSubmit={submitRequest} disabled={busy} />}
        {phase === 'ticketSearch' && <AnswerForm label="Sujet du ticket" placeholder="Ex. : erreur lors de la validation d’une facture…" value={draft} onChange={setDraft} onSubmit={submitTicketSearch} disabled={busy} submitLabel="Chercher avec Dynamix" />}
        {phase === 'clarification' && <><AnswerForm label="Précision complémentaire" placeholder={clarificationField === 'reproduction' ? 'Ex. : Sur la commande SO123, après avoir cliqué sur Valider…' : 'Ex. : Voici le message affiché et ce que j’ai essayé…'} value={draft} onChange={setDraft} onSubmit={submitClarification} disabled={busy} allowEmpty={attachments.length > 0} submitLabel={!draft.trim() && attachments.length ? 'Continuer avec les pièces jointes' : 'Continuer'} options={showScreenshotActions && <CaptureOptions addFiles={addFiles} selectScreenshot={selectScreenshot} captureBrowserTab={captureBrowserTab} disabled={busy} />} /><div className="pl-11"><button type="button" className="text-sm font-semibold text-[#6B4C9A] underline" disabled={busy} onClick={skipClarification}>Je ne peux pas répondre</button></div></>}
        {phase === 'searching' && <div className="assistance-bot-search" role="status"><DynamicsIcon size={40} state="thinking" /><span className="assistance-bot-search-dots">Je prépare votre ticket</span></div>}
        {phase === 'searchingTickets' && <div className="assistance-bot-search" role="status"><DynamicsIcon size={40} state="thinking" /><span className="assistance-bot-search-dots">Je cherche les tickets pertinents</span></div>}
        {phase === 'createError' && result && <><BotBubble>Je n’ai pas pu créer le ticket. Votre demande est conservée ici ; vous pouvez réessayer.</BotBubble><button type="button" className="assistance-primary" disabled={busy} onClick={() => createTicket(result)}>Réessayer la création</button></>}
        {phase === 'success' && <div className="assistance-bot-success"><div className="flex items-center gap-3"><DynamicsIcon size={48} state="listening" /><div className="assistance-bot-success-message rounded-2xl px-4 py-3 text-sm font-semibold">Merci pour ces précisions ! J’ai créé votre ticket{createdTicket?.id ? ` #${createdTicket.id}` : ''} et transmis votre demande à notre équipe d’assistance. Elle vous répondra dès que possible.</div></div><button type="button" className="assistance-primary mt-5" onClick={onClose}>Terminer</button></div>}
        {['occurrence', 'errorMessage', 'request', 'ticketSearch'].includes(phase) && <div className="pl-11"><button type="button" className="text-sm font-semibold text-[#6B4C9A] underline" onClick={() => { setPhase('choice'); setDraft(''); setError(''); setMessages([{ role: 'bot', text: 'Bonjour, je suis Dynamix. De quoi avez-vous besoin ?' }, ...(screenshot ? [{ role: 'screenshot', image: screenshot }] : [])]); }}>Changer le type de demande</button></div>}
      </div>
    </div>
  </div>;
}

function BotBubble({ children }) { return <div className="flex items-start gap-3"><DynamicsIcon size={32} /><div className="max-w-[85%] rounded-2xl rounded-tl-none bg-gray-100 px-4 py-3 text-sm">{children}</div></div>; }
function UserBubble({ children }) { return <div className="flex justify-end"><div className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-tr-none bg-[#6B4C9A] px-4 py-3 text-sm text-white">{children}</div></div>; }
function ScreenshotBubble({ image, disabled, onRemove }) { return <div className="assistance-screenshot-preview" aria-label="Aperçu de la capture"><img src={`data:${image.type};base64,${image.data}`} alt="Capture ajoutée au ticket" /><div><span>Capture ajoutée</span><button type="button" disabled={disabled} aria-label="Supprimer la capture" onClick={onRemove}><Trash2 size={16} /></button></div></div>; }
function AnswerForm({ label, placeholder, value, onChange, onSubmit, disabled, allowEmpty = false, submitLabel = 'Continuer', options }) { return <form onSubmit={onSubmit} className="pl-11"><label className="sr-only" htmlFor="support-answer">{label}</label><textarea id="support-answer" autoFocus rows="4" maxLength="3000" value={value} onChange={event => onChange(event.target.value)} disabled={disabled} className="w-full rounded-lg border p-3 focus:border-[#6B4C9A] focus:ring-2 focus:ring-[#6B4C9A]" placeholder={placeholder} /><div className="assistance-answer-actions">{options}<button type="submit" className="assistance-primary" disabled={disabled || (!value.trim() && !allowEmpty)}><Send size={16} />{submitLabel}</button></div></form>; }
function CaptureOptions({ addFiles, selectScreenshot, captureBrowserTab, disabled }) { return <div className="assistance-screenshot-actions-row"><label title="Joindre une capture" aria-label="Joindre une capture"><Camera size={16} />Capture<input className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" disabled={disabled} onChange={selectScreenshot} /></label><button type="button" title="Capturer un onglet de votre navigateur" aria-label="Capturer un onglet de votre navigateur" disabled={disabled} onClick={captureBrowserTab}><Camera size={16} />Capturer un onglet du navigateur</button><label title="Joindre un fichier" aria-label="Joindre un fichier"><Paperclip size={16} />Fichier<input className="sr-only" type="file" multiple disabled={disabled} onChange={addFiles} /></label></div>; }
